import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, ChevronRight, DollarSign, Plus, Search, Send, Utensils } from 'lucide-react';
import { db } from '@/lib/db';
import type { Ace, CallYourShot, Course, Game, Member, MemberPublic, MemberWithStats, PostEnriched } from '@/lib/db/types';
import { Chip } from '@/components/shared/Chip';
import { EmptyState } from '@/components/shared/EmptyState';
import { PostCard } from '@/components/feed/PostCard';
import { CallYourShotTimelineCard } from '@/components/feed/CallYourShotTimelineCard';
import { MorningTeeSheet } from '@/components/feed/MorningTeeSheet';
import { MemberSpotlight } from '@/components/feed/MemberSpotlight';

interface FeedTabProps {
  member: Member;
  onComposeRound: () => void;
  onPostStatus: () => void;
  onInviteMember: () => void;
  onCallYourShot: () => void;
  onSearchMembers: () => void;
  onUpdateHandicap: () => void;
  onOpenClubhouse: () => void;
  onOpenProfile: (id: string) => void;
  onOpenGame: (id: string) => void;
  onOpenWager: (post: PostEnriched, opts?: { isNew?: boolean }) => void;
  onOpenGameResult: (post: PostEnriched, gameIndex: number) => void;
  onOpenMoneyResults?: (post: PostEnriched) => void;
}

export function FeedTab({
  member,
  onComposeRound,
  onPostStatus,
  onInviteMember,
  onCallYourShot,
  onSearchMembers,
  onUpdateHandicap,
  onOpenClubhouse,
  onOpenProfile,
  onOpenGame,
  onOpenWager,
  onOpenGameResult,
  onOpenMoneyResults,
}: FeedTabProps) {
  const [posts, setPosts] = useState<PostEnriched[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [members, setMembers] = useState<MemberPublic[]>([]);
  const [aces, setAces] = useState<Ace[]>([]);
  const [shots, setShots] = useState<CallYourShot[]>([]);
  const [reactedShotIds, setReactedShotIds] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<MemberWithStats | null>(null);
  const [following, setFollowing] = useState<string[]>([]);
  const [filter, setFilter] = useState<'all' | 'following'>('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadGen = useRef(0);
  const hasLoaded = useRef(false);

  const load = async (opts: { silent?: boolean } = {}) => {
    const gen = ++loadGen.current;
    if (!opts.silent && !hasLoaded.current) setLoading(true);
    else if (opts.silent) setRefreshing(true);

    try {
      const [postsData, gamesData, coursesData, membersData, acesData, shotsData, statsData, followingData, reactedIds] = await Promise.all([
        db.posts.list({ limit: 50, includeCourseHoles: false }),
        db.games.list(),
        db.courses.list(),
        db.members.list({ limit: 100 }),
        db.aces.list(),
        db.callShots.list(),
        db.members.getWithStats(member.id),
        db.follows.following(),
        db.callShots.viewerReactedShotIds(),
      ]);
      if (gen !== loadGen.current) return;
      setPosts(postsData);
      setGames(gamesData);
      setCourses(coursesData);
      setMembers(membersData);
      setAces(acesData);
      setShots(shotsData);
      setStats(statsData);
      setFollowing(followingData);
      setReactedShotIds(reactedIds);
      setError(null);
      hasLoaded.current = true;
    } catch (e) {
      if (gen !== loadGen.current) return;
      setError((e as Error).message || 'Unable to load feed');
    } finally {
      if (gen !== loadGen.current) return;
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let active = true;
    hasLoaded.current = false;
    void load();
    const unsub = db.posts.subscribe(() => { if (active) void load({ silent: true }); });
    const onRefresh = () => { if (active) void load({ silent: true }); };
    const onFollowingChanged = (e: Event) => {
      if (!active) return;
      const { memberId, following: isF } = (e as CustomEvent<{ memberId: string; following: boolean }>).detail;
      setFollowing((prev) => {
        const has = prev.includes(memberId);
        if (isF && !has) return [...prev, memberId];
        if (!isF && has) return prev.filter((id) => id !== memberId);
        return prev;
      });
    };
    window.addEventListener('bagtag:refresh-feed', onRefresh);
    window.addEventListener('bagtag:following-changed', onFollowingChanged);
    return () => {
      active = false;
      loadGen.current += 1;
      unsub();
      window.removeEventListener('bagtag:refresh-feed', onRefresh);
      window.removeEventListener('bagtag:following-changed', onFollowingChanged);
    };
  }, [member.id]);

  const courseNames = useMemo(() => Object.fromEntries(courses.map((c) => [c.id, c.name])), [courses]);

  const isVisibleToMe = (post: PostEnriched) => {
    if (post.author_id === member.id) return true;
    const v = post.author.visibility || 'public';
    if (v === 'public') return true;
    return following.includes(post.author_id);
  };

  const shown = useMemo(() => {
    const base = filter === 'following'
      ? posts.filter((p) => following.includes(p.author_id) || p.author_id === member.id)
      : posts;
    return base.filter(isVisibleToMe);
  }, [posts, filter, following, member.id]);

  const pendingShots = useMemo(
    () => shots.filter((s) => s.status === 'pending'),
    [shots],
  );

  type TimelineEntry =
    | { kind: 'post'; at: string; post: PostEnriched }
    | { kind: 'shot'; at: string; shot: CallYourShot };

  const timeline = useMemo(() => {
    const items: TimelineEntry[] = [];
    shown.forEach((post) => items.push({ kind: 'post', at: post.created_at, post }));
    pendingShots.forEach((shot) => items.push({ kind: 'shot', at: shot.proposed_at, shot }));
    return items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [shown, pendingShots]);

  const courseById = useMemo(() => Object.fromEntries(courses.map((c) => [c.id, c])), [courses]);

  const shotBadge = useMemo(() => {
    return shots.filter((s) => s.status === 'pending' && s.player_id === member.id).length;
  }, [shots, member.id]);

  const hcpUpdated = member.handicap_updated_at ? new Date(member.handicap_updated_at).getTime() : null;
  const daysSinceUpdate = hcpUpdated ? Math.floor((Date.now() - hcpUpdated) / 86400000) : null;
  const hcpStale = member.handicap == null || !member.handicap_updated_at || (daysSinceUpdate != null && daysSinceUpdate > 14);
  let hcpUrgency: 'set' | 'confirm' | 'gentle' | 'firm' = 'gentle';
  if (member.handicap == null) hcpUrgency = 'set';
  else if (!member.handicap_updated_at) hcpUrgency = 'confirm';
  else if (daysSinceUpdate != null && daysSinceUpdate >= 30) hcpUrgency = 'firm';

  const meStats = stats || ({ ...member, total_points: 0, rounds_count: 0, avg_gross: null, low_gross: null, ace_count: 0, follower_count: 0, following_count: 0, giving_total_cents: 0 } as MemberWithStats);
  const memberMap = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);

  return (
    <div className="space-y-4 pt-4">
      {hcpStale && (
        <button
          type="button"
          onClick={onUpdateHandicap}
          className={`mb-3 flex w-full items-center gap-3 rounded-xl border border-gold px-3.5 py-[11px] text-left ${hcpUrgency === 'firm' ? 'bg-gold/[0.18]' : 'bg-gold/10'}`}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gold text-[10px] font-extrabold tracking-wide text-navy">HCP</div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold leading-[1.2] text-navy">
              {hcpUrgency === 'set' && 'Set your handicap'}
              {hcpUrgency === 'confirm' && 'Confirm your handicap index'}
              {hcpUrgency === 'gentle' && 'GHIN refresh — check your index?'}
              {hcpUrgency === 'firm' && 'Update your handicap'}
            </div>
            <div className="mt-0.5 text-[10.5px] leading-[1.35] text-ink-soft">
              {hcpUrgency === 'set' && 'GHIN revises indices every 1st & 15th. Enter yours to keep Nassau/Skins fair.'}
              {hcpUrgency === 'confirm' && 'Not fully integrated with GHIN yet — please confirm your current index so strokes calculate correctly.'}
              {hcpUrgency === 'gentle' && `Last updated ${daysSinceUpdate} days ago. GHIN typically refreshes every 2 weeks — verify yours is current.`}
              {hcpUrgency === 'firm' && `Last updated ${daysSinceUpdate} days ago. Time to check your latest GHIN index.`}
            </div>
          </div>
          <ChevronRight size={16} className="shrink-0 text-gold-deep" />
        </button>
      )}

      <button
        type="button"
        onClick={onOpenClubhouse}
        className="mb-3 flex w-full items-center gap-3 rounded-xl border border-line bg-paper px-3.5 py-3 text-left shadow-bt"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy/10 text-navy">
          <Utensils size={18} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-ink">Clubhouse</div>
          <div className="text-[11px] text-mute">Menu, dining &amp; food ordering</div>
        </div>
        <ChevronRight size={16} className="shrink-0 text-mute" />
      </button>

      <div className="mb-3.5 grid grid-cols-2 gap-2">
        <button type="button" onClick={onInviteMember} className="flex min-w-0 flex-col gap-1.5 rounded-[14px] bg-navy p-3 text-left text-paper shadow-bt-md">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold text-navy"><Send size={14} strokeWidth={2.4} /></div>
          <div className="font-serif text-[13px] font-bold leading-[1.15] tracking-[-0.01em]">Invite a Member</div>
          <div className="text-[10px] font-semibold text-white/60">Vouch for a friend</div>
        </button>
        <button type="button" onClick={onCallYourShot} className="relative flex min-w-0 flex-col gap-1.5 rounded-[14px] border-2 border-gold bg-paper p-3 text-left text-ink shadow-bt">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gold text-navy"><DollarSign size={16} /></div>
          <div className="font-serif text-[13px] font-bold leading-[1.15] tracking-[-0.01em]">Call Your Shot</div>
          <div className="text-[10px] font-semibold text-mute">Set your target</div>
          {shotBadge > 0 && (
            <span className="absolute right-2 top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-bad px-1 text-[10px] font-bold text-paper">{shotBadge}</span>
          )}
        </button>
        <button
          type="button"
          onClick={onComposeRound}
          className="flex min-w-0 flex-col gap-1.5 rounded-[14px] border border-gold-deep bg-gradient-to-br from-gold to-gold-deep p-3 text-left text-navy"
          style={{ boxShadow: '0 2px 8px rgba(196,166,98,0.35)' }}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-gold"><Plus size={18} strokeWidth={2.6} /></div>
          <div className="font-serif text-[13px] font-extrabold leading-[1.15] tracking-[-0.01em]">Post a Round</div>
          <div className="text-[10px] font-semibold text-navy/70">Score + scorecard</div>
        </button>
        <button type="button" onClick={onPostStatus} className="flex min-w-0 flex-col gap-1.5 rounded-[14px] border border-line bg-paper p-3 text-left text-ink shadow-bt">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg text-gold-deep" style={{ background: 'rgba(196,166,98,0.18)' }}>
            <Camera size={16} strokeWidth={2.4} />
          </div>
          <div className="font-serif text-[13px] font-bold leading-[1.15] tracking-[-0.01em]">Post</div>
          <div className="text-[10px] text-mute">Photo or status</div>
        </button>
      </div>

      <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1">
        <Chip active={filter === 'all'} onClick={() => setFilter('all')}>All Members</Chip>
        <Chip active={filter === 'following'} onClick={() => setFilter('following')}>Following</Chip>
        <button
          type="button"
          onClick={onSearchMembers}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-line bg-paper px-3.5 py-2 text-[13px] font-semibold text-ink shadow-bt"
        >
          <Search size={14} /> Member Search
        </button>
      </div>

      <MorningTeeSheet games={games} posts={posts} memberMap={memberMap} courseNames={courseNames} onOpenGame={onOpenGame} />

      <MemberSpotlight
        members={members}
        posts={posts}
        aces={aces}
        me={meStats}
        following={following}
        courseNames={courseNames}
        onOpenProfile={onOpenProfile}
      />

      {loading && <div className="rounded-[14px] bg-paper p-6 text-center text-sm text-mute shadow-bt">Loading feed…</div>}
      {refreshing && !loading && (
        <div className="text-center text-xs font-medium text-mute">Updating…</div>
      )}
      {error && <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {!loading && shown.length === 0 && pendingShots.length === 0 && (
        <EmptyState text="No rounds in your feed yet. Follow a few members." />
      )}

      <div className="space-y-3">
        {timeline.map((item, i) => {
          if (item.kind === 'post') {
            return (
              <PostCard
                key={`post-${item.post.id}`}
                post={item.post}
                memberMap={memberMap}
                viewerId={member.id}
                staggerIndex={i}
                onOpenProfile={onOpenProfile}
                onOpenWager={onOpenWager}
                onOpenGameResult={onOpenGameResult}
                onOpenMoneyResults={onOpenMoneyResults}
                onPostDeleted={(id) => setPosts((prev) => prev.filter((p) => p.id !== id))}
                onPostUpdated={(id, body) => setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, body } : p)))}
              />
            );
          }
          const caller = memberMap[item.shot.player_id] ?? { name: 'Member' };
          return (
            <CallYourShotTimelineCard
              key={`shot-${item.shot.id}`}
              shot={item.shot}
              member={member}
              caller={caller}
              course={item.shot.course_id ? courseById[item.shot.course_id] : null}
              reacted={reactedShotIds.has(item.shot.id)}
              staggerIndex={i}
              onCallYours={onCallYourShot}
              onOpenProfile={onOpenProfile}
              onReacted={() => void load({ silent: true })}
            />
          );
        })}
      </div>
    </div>
  );
}
